import React, { FunctionComponent } from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import PermissionHelpSteps from "../PermissionHelpSteps";

interface PermissionBlockedNoticeProps {
  title: string;
  description: string;
  onCheckAgain: () => void;
  checking: boolean;
  /** Set after a check that did not clear the block; offers a reload, which some browsers need before a changed setting applies. */
  stillBlocked: boolean;
  /** Hide the per-browser steps for a failure that is not a blocked permission (a timeout, no fix). Default: shown. */
  showSteps?: boolean;
  actionLabel?: string;
}

/** The red notice shown when the camera or location cannot be used, with the steps to allow them and a way to re-test. */
const PermissionBlockedNotice: FunctionComponent<PermissionBlockedNoticeProps> = ({
  title,
  description,
  onCheckAgain,
  checking,
  stillBlocked,
  showSteps = true,
  actionLabel = "Check again"
}) => (
  <div className="flex flex-col gap-3 rounded-lg bg-error-2 p-3 text-sm text-error-1">
    <div className="flex gap-2">
      <HiOutlineExclamationCircle className="mt-0.5 flex-shrink-0 text-base" />
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">{title}</span>
        <span>{description}</span>
      </div>
    </div>
    {showSteps && <PermissionHelpSteps />}
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <button
        type="button"
        onClick={onCheckAgain}
        disabled={checking}
        className="cursor-pointer rounded-md border border-solid border-error-1 bg-white px-3 py-1 text-xs"
      >
        {checking ? "Checking…" : actionLabel}
      </button>
      <span aria-live="polite" className="text-xs">
        {stillBlocked && !checking && (
          <>
            Still not working. Turn it on in the site settings, then check again, or{" "}
            <button type="button" onClick={() => window.location.reload()} className="cursor-pointer border-0 bg-transparent p-0 underline focus:no-underline">
              reload the page
            </button>
            .
          </>
        )}
      </span>
    </div>
  </div>
);

export default PermissionBlockedNotice;
